import { DateFilter, FilterCheckbox } from '@companio/companio_ui';
import {
  FILTER_GROUP_DOCUMENT_MATCHING,
  FILTER_GROUP_DOCUMENT_STATUS,
  DOCUMENT_MATCHING_FILTERS,
  DOCUMENT_STATUS_FILTERS,
  FILTER_GROUP_AGREEMENT_TYPE,
  FILTER_GROUP_AGREEMENT_STATUS,
  AGREEMENT_TYPE_FILTERS,
  AGREEMENT_STATUS_FILTERS,
  OTHER_DOCUMENT_TYPE_FILTERS
} from '@/constants/filters';
import { DocumentStatus, OtherDocumentTypes } from '@/types/document';
import { IMAGE_MIME_TYPES } from '@/constants/files';
import { Member, User } from '@/types/user';
import { COUNTRIES, CURRENCIES } from '@/constants/countries';
import { DEFAULT_CURRENCY } from '@/constants/documents';
import { LoanAgreementStatus, LoanAgreementType } from '@/types/loan_agreements';

export const capitalize = (s: string) => {
  return s.replace(/\b\w/g, (l) => l.toUpperCase());
};

export const capitalizeFirstLetter = (s: string) => {
  return (s && s.charAt(0).toUpperCase() + s.slice(1)) || '';
};

export function capitalizeFirstLetterOfEachWord(input: string): string {
  const words = input.split(' ');

  const capitalizedWords = words.map((word) => {
    if (word.length > 0) {
      return capitalizeFirstLetter(word);
    }
    return word;
  });

  return capitalizedWords.join(' ');
}

export const isValidString = (s: any): boolean => {
  if (s === null) {
    return false;
  }
  if (typeof s === 'string' || (typeof s === 'object' && s.constructor === String)) {
    return s.length > 0;
  }
  return false;
};

export const isValidObject = (object: any): boolean => {
  if (object === null) {
    return false;
  }
  return typeof object === 'object';
};

export const isValidEmail = (email: any): boolean => {
  if (email === null) {
    return false;
  }
  if (!isValidString(email)) {
    return false;
  }

  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const isValidNumber = (value: any) => {
  if (value === null) {
    return false;
  }
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(parseFloat(value));
};

export function isValidDate(dateOrString: string | Date | undefined | null) {
  if (!dateOrString) {
    return false;
  }
  if (
    dateOrString instanceof Date ||
    Object.prototype.toString.call(dateOrString) === '[object Date]'
  ) {
    // Date object
    return true;
  }
  if (typeof dateOrString === 'string') {
    const yyyymmddhhmm = yyyymmddAndHHMMStringToDate(dateOrString);
    if (yyyymmddhhmm || Object.prototype.toString.call(yyyymmddhhmm) === '[object Date]') {
      return true;
    }

    // YYYY-MM-DD
    const dateFromString = yyyymmddhhmmssStringToDate(dateOrString);
    if (
      dateFromString instanceof Date ||
      Object.prototype.toString.call(dateFromString) === '[object Date]'
    ) {
      return true;
    }

    // last chance
    const iso8601Date = new Date(dateOrString);
    return iso8601Date instanceof Date && !Number.isNaN(iso8601Date.getTime());
  }
  return false;
}

export function yyyymmddAndHHMMStringToDate(dateString: string) {
  const arr = dateString.split(/-|\s|:/); // split string and create array.
  if (Array.isArray(arr) && arr.length === 5) {
    const year = arr[0];
    const month = `0${arr[1]}`.slice(-2);
    const day = `0${arr[2]}`.slice(-2);
    const hour = `0${arr[3]}`.slice(-2);
    const minute = `0${arr[4]}`.slice(-2);
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00+00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function yyyymmddhhmmssStringToDate(dateString: string) {
  const arr = dateString.split(/-|\s|:/); // split string and create array.
  if (!Array.isArray(arr) || arr.length !== 6) {
    return null;
  }
  const year = arr[0];
  const month = `0${arr[1]}`.slice(-2);
  const day = `0${arr[2]}`.slice(-2);
  const hour = `0${arr[3]}`.slice(-2);
  const minute = `0${arr[4]}`.slice(-2);
  const second = `0${arr[5]}`.slice(-2);
  const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getDateFromDateOrString(dateOrString: string | Date | undefined) {
  if (!dateOrString) {
    return undefined;
  }

  // If it's already a Date object, return it if valid
  if (dateOrString instanceof Date) {
    return Number.isFinite(dateOrString.getTime()) ? dateOrString : undefined;
  }

  // Try parsing as ISO string first
  const isoDate = new Date(dateOrString);
  if (Number.isFinite(isoDate.getTime())) {
    return isoDate;
  }

  // Try other formats if ISO parsing fails
  const yyyymmddhhmm = yyyymmddAndHHMMStringToDate(dateOrString);
  if (yyyymmddhhmm && Number.isFinite(yyyymmddhhmm.getTime())) {
    return yyyymmddhhmm;
  }

  const dateFromString = yyyymmddhhmmssStringToDate(dateOrString);
  if (dateFromString && Number.isFinite(dateFromString.getTime())) {
    return dateFromString;
  }

  return undefined;
}
export const isPositiveNumber = (value: any) => {
  if (value === null) {
    return false;
  }
  return isValidNumber(value) && value > 0;
};

export function validImageMimeType(mimeType: string) {
  return IMAGE_MIME_TYPES.indexOf(mimeType) >= 0;
}

export function removeDocumentTypePrefix(name: string, type: string) {
  return name.replace(new RegExp(`${type}_\\d+_`, 'i'), '');
}

export const formatNumberAmount = (value: number) => {
  return parseFloat((value / 100) as unknown as string).toFixed(2);
};

export const getFilterObject = (filters: FilterCheckbox[]) => {
  const activeFilters = filters.filter((filter) => filter.checked);
  const statusFilters = activeFilters
    .filter((filter) => DOCUMENT_STATUS_FILTERS.includes(filter.id as DocumentStatus))
    .map((filter) => filter.id);
  const matchingFilters = activeFilters
    .filter((filter) => DOCUMENT_MATCHING_FILTERS.includes(filter.id))
    .map((filter) => filter.id);

  return {
    ...(statusFilters.length > 0 && { [FILTER_GROUP_DOCUMENT_STATUS]: statusFilters }),
    ...(matchingFilters.length > 0 && { [FILTER_GROUP_DOCUMENT_MATCHING]: matchingFilters })
  };
};

export const getTypeObject = (filters: FilterCheckbox[]) => {
  const activeFilters = filters.filter((filter) => filter.checked);
  const typeFilters = activeFilters
    .filter((filter) => OTHER_DOCUMENT_TYPE_FILTERS.includes(filter.id as OtherDocumentTypes))
    .map((filter) => filter.id);

  return typeFilters.length > 0 && typeFilters;
};

export const getAgreementsFilterObject = (filters: FilterCheckbox[]) => {
  const activeFilters = filters.filter((filter) => filter.checked);
  const statusFilters = activeFilters
    .filter((filter) => AGREEMENT_STATUS_FILTERS.includes(filter.id as LoanAgreementStatus))
    .map((filter) => filter.id);
  const typeFilters = activeFilters
    .filter(
      (filter) =>
        AGREEMENT_TYPE_FILTERS.includes(filter.id as LoanAgreementType) &&
        filter.id !== LoanAgreementType.ACTIVE
    )
    .map((filter) => filter.id);

  const loanActive = activeFilters.find((filter) => filter.id === LoanAgreementType.ACTIVE);

  if (loanActive) {
    statusFilters.push(
      LoanAgreementStatus.CREATED,
      LoanAgreementStatus.STARTED,
      LoanAgreementStatus.IN_PROGRESS
    );
  }

  return {
    ...(statusFilters.length > 0 && { [FILTER_GROUP_AGREEMENT_STATUS]: statusFilters }),
    ...(typeFilters.length > 0 && { [FILTER_GROUP_AGREEMENT_TYPE]: typeFilters })
  };
};

export const removeSnakeCase = (string: string) => {
  return string.replace(/_/g, ' ');
};

export function facebookShareURL(url: string) {
  return `https://www.facebook.com/sharer.php?u=${url}`;
}

export function twitterShareURL(message: string, url: string) {
  return `https://www.twitter.com/intent/tweet?url=${url}&text=${message}`;
}

export function linkedinShareURL(url: string) {
  const encodedUrl = encodeURIComponent(url);
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
}

export function whatsappShareURL(message: string, url: string) {
  const content = `${message}${url}`;
  return `https://wa.me/?text=${content}`;
}

export function telegramShareURL(message: string, url: string) {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`;
}

export function socialMediaSharePopUp(url: string) {
  const left = (window.screen.width - 570) / 2;
  const top = (window.screen.height - 570) / 2;
  const params = `menubar=no,toolbar=no,status=no,width=570,height=570,top=${top},left=${left}`;

  window.open(url, 'NewWindow', params);
}

export function formatAdminName(fullName: string): string {
  if (!fullName) return fullName;

  const cleaned = fullName.replace(/\(.*?\)/g, '').trim();

  const parts = cleaned
    .split(' ')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length <= 1) return parts[0];

  const firstName = parts[0];
  const lastNameInitial = parts[1][0].toUpperCase();

  return `${firstName} ${lastNameInitial}.`;
}

/**
 * Retrieves an item from local storage and checks if it has expired.
 * If the item does not exist or has expired, it will be removed from local storage.
 *
 * @param {string} key The key of the item in local storage.
 * @returns The value of the item if it has not expired, otherwise null.
 */
export function checkLocalStorageItemExpired(key: string) {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  const now = new Date();
  const item = JSON.parse(itemStr);

  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

/**
 * Creates a new item in local storage with an expiration date.
 * @param {object} options
 * @param {string} options.key - The key to store the item under.
 * @param {boolean} options.value - The value to store.
 * @param {number} options.ttl - The time to live in milliseconds.
 */
export function createLocalStorageItemWithExpirationDate({
  key,
  value,
  ttl
}: {
  key: string;
  value: boolean;
  ttl: number;
}) {
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + ttl
  };

  localStorage.setItem(key, JSON.stringify(item));
}

export const getGeneralFilterObject = (filters: FilterCheckbox[]) => {
  const activeFilters = filters.filter((filter) => filter.checked);
  const filtersId = activeFilters.map((filter) => filter.id);

  return filtersId;
};

export function groupSelectedFiltersByType(
  initialFilters: Record<string, string[]>,
  filters: FilterCheckbox[]
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const key in initialFilters) {
    result[key] = [];
  }

  const activeFilters = filters.filter((filter) => filter.checked);

  for (const filter of activeFilters) {
    const { filterBy, id } = filter;

    if (result.hasOwnProperty(filterBy as string)) {
      result[filterBy as string].push(id);
    }
  }
  return result;
}
interface StringEllipsisProps {
  str: string;
  start: number;
  end: number;
}

function stringEllipsis({ str, start, end }: StringEllipsisProps) {
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return str;
  }
  if (start <= 0 || end <= 0) {
    return str;
  }
  if (str.length <= start + end) {
    return str;
  }

  let result = str.substring(0, start);
  result += '...';
  result += str.substring(end, str.length);
  return result;
}

export function addEllipsisToStringIfLengthGreaterThan({
  string,
  length
}: {
  string: string;
  length: number;
}) {
  if (!isValidString(string) || string.length <= length) {
    return string;
  }

  const start = Math.floor(length / 2) - 3;
  const end = string.length - (length - start - 3);
  return stringEllipsis({ str: string, start, end });
}

export function fullNameForUser(userData: User | Member, initializeLastName?: boolean): string {
  if (userData.first_name) {
    if (userData.last_name) {
      return initializeLastName
        ? `${userData.first_name} ${userData.last_name.charAt(0)}`
        : `${userData.first_name} ${userData.last_name}`;
    }
    return userData.first_name;
  }

  return userData.email || 'Unknown';
}

function dataForCurrencyISOCode(code: string) {
  const match = CURRENCIES.filter((c) => {
    return c.code === code;
  });
  if (match.length > 0) {
    return match[0];
  }
  return null;
}

export function symbolForCurrencyISOCode(code: string) {
  // CURRENCIES only lists fiat ISO codes — crypto currencies (USDT, XXBT, ETH, ...) have no
  // entry and no "symbol", so fall back to the code itself instead of an undefined actualSymbol.
  const match = dataForCurrencyISOCode(code ?? DEFAULT_CURRENCY);
  return match?.actualSymbol ?? code;
}

export function isEmptyObject(obj: any) {
  return (
    obj === null || typeof obj !== 'object' || Array.isArray(obj) || Object.keys(obj).length === 0
  );
}
function dataForCountryCode(countryCode: string) {
  const match = COUNTRIES.filter((c) => {
    return c.code === countryCode;
  });
  if (match.length > 0) {
    return match[0];
  }
  return null;
}

function dataForCountryName(countryName: string) {
  const match = COUNTRIES.filter((c) => {
    return c.name === countryName;
  });
  if (match.length > 0) {
    return match[0];
  }
  return null;
}

export function countryNameForCountryCode(countryCode: string) {
  const match = dataForCountryCode(countryCode);
  return match !== null ? match.name : countryCode;
}

export function countryCodeForCountryName(countryName: string) {
  const match = dataForCountryName(countryName);
  return match !== null ? match.code : countryName;
}

export function normalizeToArray(value?: string | string[]): string[] | undefined {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value;
  return undefined;
}

export function getSafeValue<T>(value: T | null | undefined, defaultValue: T): T {
  return value !== null && value !== undefined ? value : defaultValue;
}

export function reverseObjectKeyValues(obj: any) {
  const newObj: any = {};
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    newObj[obj[k]] = k;
  }
  return newObj;
}

export function getSafeDateFilter(storageKey: string): DateFilter {
  try {
    const storedDate = localStorage.getItem(storageKey)
      ? JSON.parse(localStorage.getItem(storageKey) as string)
      : null;
    if (storedDate && typeof storedDate.month === 'number' && typeof storedDate.year === 'number') {
      return {
        month: storedDate.month,
        year: storedDate.year
      };
    }
    return {
      month: new Date().getMonth(),
      year: new Date().getFullYear()
    };
  } catch (e) {
    console.error('Failed to parse purchaseLocalStorageDate', e);
  }

  // Fallback to current date
  const now = new Date();
  return {
    month: now.getMonth(),
    year: now.getFullYear()
  };
}

export function addEllipsisToEndOfString({ string, length }: { string: string; length: number }) {
  if (!isValidString(string) || string.length <= length) {
    return string;
  }

  return `${string.substring(0, length)}...`;
}
